import { FormEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import OrganizationNoteService, {
    OrganizationNote
} from "../../services/OrganizationNoteService";
import styles from "./OrganizationNotesPanel.module.css";

interface OrganizationNotesPanelProps {
    fhirBaseUrl: string;
    organizationReference: string;
    organizationName: string;
}

const OrganizationNotesPanel: FunctionComponent<OrganizationNotesPanelProps> = ({
    fhirBaseUrl,
    organizationReference,
    organizationName
}) => {
    const service = useMemo(
        () => new OrganizationNoteService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );
    const [notes, setNotes] = useState<OrganizationNote[]>([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(undefined);

        service.getNotes(organizationReference)
            .then((result) => {
                if (mounted) setNotes(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger les notes du pôle.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [organizationReference, service]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        if (!text.trim() || submitting) return;

        setSubmitting(true);
        setError(undefined);

        try {
            const updatedNotes = await service.addNote(organizationReference, text);
            setNotes(updatedNotes);
            setText("");
        } catch (reason) {
            console.error(reason);
            setError("La note n'a pas pu être ajoutée. La saisie a été conservée.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className={styles.panel} aria-label={`Notes de ${organizationName}`}>
            <header className={styles.header}>
                <div>
                    <h2>Notes du pôle</h2>
                    <p>{organizationName}</p>
                </div>
            </header>

            <form className={styles.form} onSubmit={handleSubmit}>
                <textarea
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    placeholder="Ajouter une note..."
                    aria-label="Nouvelle note"
                    rows={2}
                    disabled={submitting}
                />
                <button
                    type="submit"
                    disabled={!text.trim() || submitting}
                >
                    {submitting ? "Ajout..." : "Ajouter"}
                </button>
            </form>

            {error && <p className={styles.error} role="alert">{error}</p>}

            {loading ? (
                <p className={styles.state}>Chargement des notes...</p>
            ) : notes.length === 0 ? (
                <p className={styles.empty}>Aucune note pour le moment.</p>
            ) : (
                <div className={styles.list}>
                    {notes.map((note) => (
                        <article key={note.id} className={styles.noteCard}>
                            <div className={styles.noteContent}>
                                <span className={styles.bullet} aria-hidden="true">•</span>
                                <p>{note.text}</p>
                            </div>
                            {(note.time || note.author) && (
                                <footer>
                                    {note.time && <time dateTime={note.time}>{formatNoteDate(note.time)}</time>}
                                    {note.author && <span>{note.author}</span>}
                                </footer>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
};

const formatNoteDate = (value: string): string => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric"
    }).format(date);
};

export default OrganizationNotesPanel;
